import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import type { Quote } from '@furni/shared';
import { Shell } from '../components/Shell';
import { Ornament } from '../components/Card';
import { RevealMontage, toRevealItems, type RevealItem } from '../components/RevealMontage';
import { analyzeImage, ApiError } from '../lib/api';
import { useCopy } from '../lib/settings';
import { preloadAll, preloadImage } from '../lib/preloadImages';
import { uploadStore } from '../lib/uploadStore';

export const Route = createFileRoute('/analyzing')({
  component: AnalyzingPage,
});

type Phase = 'searching' | 'revealing';

const MIN_REVEAL_MS = 3200;
const PRELOAD_GRACE_MS = 2500;
const STATUS_ROTATE_MS = 6000;
const DELAY_NOTICE_MS = 30_000;

function AnalyzingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [statusIndex, setStatusIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>('searching');
  const [showDelayNotice, setShowDelayNotice] = useState(false);
  const [quote, setQuote] = useState<Quote | null>(null);
  const [revealItems, setRevealItems] = useState<RevealItem[]>([]);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const firedRef = useRef(false);
  const loadingTitle = useCopy('copy.loadingTitle');

  const statuses = t('loading.statuses', { returnObjects: true }) as string[];

  // Create an object URL for the uploaded image (for the scanning-beam preview)
  useEffect(() => {
    const file = uploadStore.get();
    if (!file) return;
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, []);

  useEffect(() => {
    if (phase !== 'searching') return;
    const id = setInterval(
      () => setStatusIndex((i) => (i + 1) % statuses.length),
      STATUS_ROTATE_MS,
    );
    return () => clearInterval(id);
  }, [statuses.length, phase]);

  useEffect(() => {
    if (phase !== 'searching' || error) return;
    const timer = setTimeout(() => setShowDelayNotice(true), DELAY_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [phase, error]);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;

    const file = uploadStore.get();
    if (!file) {
      void navigate({ to: '/' });
      return;
    }

    analyzeImage(file)
      .then(async (q) => {
        uploadStore.clear();
        setQuote(q);

        const candidateUrls = q.analysis.similar_examples
          .map((e) => e.image_url)
          .filter((u): u is string => Boolean(u));

        const preloadPromise = preloadAll(candidateUrls);
        const gracePromise = new Promise<string[]>((resolve) =>
          setTimeout(() => resolve([]), PRELOAD_GRACE_MS),
        );

        void preloadImage(q.image_url).catch(() => null);

        const loadedUrls = await Promise.race([preloadPromise, gracePromise]);
        const valid = new Set(loadedUrls);

        const userSeed: RevealItem = { image_url: q.image_url, emphasis: true };
        const fromExamples = toRevealItems(q.analysis.similar_examples, valid);
        const items = [userSeed, ...fromExamples];

        setRevealItems(items);
        setPhase('revealing');
        setShowDelayNotice(false);

        preloadPromise.then((finalLoaded) => {
          const finalValid = new Set(finalLoaded);
          if (finalValid.size === valid.size) return;
          setRevealItems([userSeed, ...toRevealItems(q.analysis.similar_examples, finalValid)]);
        });
      })
      .catch((err: unknown) => {
        setShowDelayNotice(false);
        if (err instanceof ApiError) {
          if (err.code === 'NOT_FURNITURE') {
            setError(t('upload.errors.not_furniture'));
            return;
          }
          if (err.code === 'OUT_OF_SCOPE') {
            setError(t('upload.errors.out_of_scope'));
            return;
          }
        }
        setError(err instanceof Error ? err.message : t('upload.errors.generic'));
      });
  }, [navigate, t]);

  useEffect(() => {
    if (phase !== 'revealing' || !quote) return;
    const totalMs = Math.max(MIN_REVEAL_MS, 900 * Math.max(revealItems.length, 2));
    const timer = setTimeout(() => {
      void navigate({ to: '/quote/$id', params: { id: quote.id } });
    }, totalMs);
    return () => clearTimeout(timer);
  }, [phase, quote, navigate, revealItems.length]);

  return (
    <Shell>
      <div className="pt-6 flex flex-col items-center text-center space-y-7 min-h-[70vh]">
        <AnimatePresence mode="wait">
          {error ? null : phase === 'searching' ? (
            <SearchingPhase
              key="searching"
              title={loadingTitle}
              status={statuses[statusIndex] ?? ''}
              statusIndex={statusIndex}
              previewUrl={previewUrl}
              showDelayNotice={showDelayNotice}
              delayNotice={t('loading.delay_notice')}
            />
          ) : quote ? (
            <RevealingPhase
              key="revealing"
              title={t('loading.reveal_title')}
              subtitle={t('loading.reveal_subtitle')}
              items={revealItems}
            />
          ) : null}
        </AnimatePresence>

        {error ? (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-cream border border-oud-200 rounded-3xl p-6 text-center space-y-5 max-w-xs w-full my-auto shadow-soft"
          >
            <div className="flex justify-center">
              <div className="w-14 h-14 rounded-full bg-oud-50 border border-oud-100 text-oud-500 flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6" aria-hidden>
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M8 8l8 8M16 8l-8 8" />
                </svg>
              </div>
            </div>
            <p className="text-sm text-oud-800 leading-relaxed">{error}</p>
            <button
              type="button"
              onClick={() => void navigate({ to: '/' })}
              className="inline-flex items-center justify-center gap-2 rounded-2xl font-bold h-11 px-5 text-sm bg-oud-800 text-sand hover:bg-oud-900 transition-all active:scale-[0.98] w-full"
            >
              {t('upload.errors.try_again')}
            </button>
          </motion.div>
        ) : null}
      </div>
    </Shell>
  );
}

function SearchingPhase({
  title,
  status,
  statusIndex,
  previewUrl,
  showDelayNotice,
  delayNotice,
}: {
  title: string;
  status: string;
  statusIndex: number;
  previewUrl: string | null;
  showDelayNotice: boolean;
  delayNotice: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8, scale: 0.98 }}
      transition={{ duration: 0.45 }}
      className="flex flex-col items-center space-y-7 w-full"
    >
      <ScanningPreview previewUrl={previewUrl} />

      <div className="space-y-3">
        <h1 className="text-2xl font-black text-oud-900">{title}</h1>
        <div className="relative min-h-12 w-full max-w-xs">
          <AnimatePresence mode="wait">
            <motion.p
              key={statusIndex}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.4 }}
              className="absolute inset-x-0 text-oud-700 leading-snug px-2"
            >
              {status}
            </motion.p>
          </AnimatePresence>
        </div>
        <div className="flex items-center gap-2 justify-center opacity-60">
          <Ornament className="w-16" />
          <span className="w-1.5 h-1.5 rounded-full bg-gold" />
          <Ornament className="w-16" />
        </div>
        {showDelayNotice ? (
          <motion.p
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-sm text-oud-600 bg-cream border border-oud-100 rounded-2xl px-4 py-2 max-w-sm mx-auto leading-relaxed"
          >
            {delayNotice}
          </motion.p>
        ) : null}
      </div>
    </motion.div>
  );
}

function ScanningPreview({ previewUrl }: { previewUrl: string | null }) {
  return (
    <div className="relative w-full max-w-xs aspect-[4/5] rounded-3xl overflow-hidden shadow-quote border border-oud-100 bg-oud-900">
      {previewUrl ? (
        <img
          src={previewUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 najdi-pattern opacity-50" aria-hidden />
      )}
      <motion.span
        className="absolute top-4 right-4 w-3 h-3 rounded-full bg-gold pointer-events-none"
        style={{ boxShadow: '0 0 12px rgba(201,144,78,0.6)' }}
        animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.3, 0.8] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        aria-hidden
      />
    </div>
  );
}

function RevealingPhase({
  title,
  subtitle,
  items,
}: {
  title: string;
  subtitle: string;
  items: RevealItem[];
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.45 }}
      className="flex flex-col items-center space-y-8 w-full"
    >
      <div className="space-y-2">
        <h1 className="text-2xl font-black text-oud-900">{title}</h1>
        <p className="text-sm text-oud-600">{subtitle}</p>
      </div>

      <RevealMontage items={items} />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.4 }}
        className="flex items-center gap-2 text-gold-deep text-sm font-bold"
      >
        <span className="inline-block w-2 h-2 rounded-full bg-gold animate-pulse" />
        <span>…</span>
      </motion.div>
    </motion.div>
  );
}
